import React, { useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

/**
 * Structured requirement-gathering card (the agent's ask_user tool).
 * One question per page: options with descriptions, a free-text "Other",
 * Previous / Skip / Next. Answers go back as one QUESTION_FORM_RESPONSE so
 * the agent drafts from real requirements instead of inventing generic copy.
 */
const QuestionFormBlock = observer(({ block }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null); // option index | "other" | null
  const [otherText, setOtherText] = useState("");

  const questions = block.questions || [];

  if (block.done) {
    const summary = (block.answers || [])
      .map(answer => answer.answer)
      .filter(Boolean)
      .join(" · ");
    return (
      <div className="copilotQForm copilotQForm--done">
        <i className="material-icons-outlined">check_circle</i>
        <span>
          {block.skipped
            ? "Answered in chat instead"
            : summary || "Skipped — using defaults"}
        </span>
      </div>
    );
  }

  const question = questions[pageIndex];
  if (!question) return null;
  const isLast = pageIndex === questions.length - 1;

  const commit = answerLabel => {
    const nextAnswers = [
      ...answers,
      { question: question.question, answer: answerLabel }
    ];
    if (isLast) {
      copilotStore.submitQuestionForm(block.id, nextAnswers);
    } else {
      setAnswers(nextAnswers);
      setPageIndex(pageIndex + 1);
      setSelected(null);
      setOtherText("");
    }
  };

  const handleNext = () => {
    if (selected === "other") commit(otherText.trim() || null);
    else if (selected !== null) commit(question.options[selected].label);
  };

  const handlePrevious = () => {
    if (pageIndex === 0) return;
    setPageIndex(pageIndex - 1);
    setAnswers(answers.slice(0, -1));
    setSelected(null);
    setOtherText("");
  };

  return (
    <div className="copilotQForm">
      <div className="copilotQForm__head">
        {question.header && (
          <span className="copilotQForm__chip">{question.header}</span>
        )}
        <span className="copilotQForm__question">{question.question}</span>
        {questions.length > 1 && (
          <span className="copilotQForm__count">
            {pageIndex + 1}/{questions.length}
          </span>
        )}
      </div>
      <div className="copilotQForm__options">
        {question.options.map((option, index) => (
          <button
            type="button"
            key={option.label}
            className={`copilotQForm__option ${
              selected === index ? "selected" : ""
            }`}
            onClick={() => setSelected(index)}
          >
            <span className="copilotQForm__optionLabel">{option.label}</span>
            {option.description && (
              <span className="copilotQForm__optionDesc">
                {option.description}
              </span>
            )}
          </button>
        ))}
        <div
          className={`copilotQForm__other ${
            selected === "other" ? "selected" : ""
          }`}
        >
          <input
            value={otherText}
            placeholder="Other — type your own"
            onFocus={() => setSelected("other")}
            onChange={event => {
              setOtherText(event.target.value);
              setSelected("other");
            }}
            onKeyDown={event => {
              if (event.key === "Enter") handleNext();
            }}
          />
        </div>
      </div>
      <div className="copilotQForm__footer">
        <button
          type="button"
          className="copilotQForm__ghostBtn"
          disabled={pageIndex === 0}
          onClick={handlePrevious}
        >
          Previous
        </button>
        <div className="copilotQForm__footerRight">
          <button
            type="button"
            className="copilotQForm__ghostBtn"
            onClick={() => commit(null)}
          >
            Skip
          </button>
          <button
            type="button"
            className="copilotQForm__primaryBtn"
            disabled={selected === null}
            onClick={handleNext}
          >
            {isLast ? "Submit" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
});

export default QuestionFormBlock;
